import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { saveFirstName, saveEmail } from '../../redux/actions/myAccountAction'
import { loginUserWithOtp } from '../../redux/actions/userAction'
import FormInput from '../../containers/form/FormInput'
import NotificationBox from '../../containers/form/NotificationBox'
import { AppUseSelector } from '../../redux/hooks'
import LanguageSection from '../setting/LanguageSection'

const MyAccount: React.FC = () => {
  const { t } = useTranslation('account')
  const { credentials } = AppUseSelector(({ user }) => user)
  const firstNameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  const [isEditFirstName, setIsEditFirstName] = useState(false)
  const [firstNameSubmitting, setFirstNameSubmitting] = useState(false)
  const [isEditEmail, setIsEditEmail] = useState(false)
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [firstName, setFirstName] = useState(
    credentials.customFields?.['first-name'] || credentials.metaData?.firstName,
  )
  const [email, setEmail] = useState(credentials.email)
  const [notify, setNotify] = useState({ label: '', isError: false })

  useEffect(() => {
    const _firstName = credentials.customFields?.['first-name'] || credentials.metaData?.firstName
    setFirstName(_firstName)
  }, [credentials.customFields?.['first-name'], credentials.metaData?.firstName])

  useEffect(() => {
    setEmail(credentials.email)
  }, [credentials.email])

  const changeFirstName = (e) => {
    setFirstName(e.target.value)
    clearNotify()
  }

  const notifySuccess = () => {
    setNotify({ label: t('profile.saved'), isError: false })
  }
  const _saveFirstName = () => {
    if (!firstNameSubmitting) {
      setFirstNameSubmitting(true)
      if (firstName === credentials.metaData?.firstName) {
        setFirstNameSubmitting(false)
        setIsEditFirstName(false)
        return
      }

      saveFirstName({ firstName })
        .then(() => {
          setFirstNameSubmitting(false)
          notifySuccess()
          setIsEditFirstName(false)
        })
        .catch((e) => {
          setFirstNameSubmitting(false)
          setNotify({ label: e || t('profile.firstNameError'), isError: true })
          setIsEditFirstName(true)
          firstNameRef?.current?.focus()
        })
    }
  }

  const changeEmail = (e) => {
    setEmail(e.target.value)
    clearNotify()
  }

  const _saveEmail = () => {
    if (!emailSubmitting) {
      setEmailSubmitting(true)
      if (email === credentials.email) {
        setEmailSubmitting(false)
        setIsEditEmail(false)
        return
      }
      saveEmail({ email })
        .then((response) => {
          if (response?.email) {
            loginUserWithOtp({ userDetails: response || null })
            setEmailSubmitting(false)
            notifySuccess()
            setIsEditEmail(false)
            return
          } else {
            setEmailSubmitting(false)
            setNotify({ label: t('profile.emailInUse'), isError: true })
            setIsEditEmail(true)
            emailRef?.current?.focus()
          }
        })
        .catch((e) => {
          setEmailSubmitting(false)
          setNotify({ label: e || t('profile.emailError'), isError: true })
          setIsEditEmail(true)
          emailRef?.current?.focus()
        })
    }
  }

  const clearNotify = () => {
    setNotify({ label: '', isError: true })
  }

  return (
    <>
      <h4 className={'m-h4 m-gray-1'}>{t('profile.title')}</h4>
      <NotificationBox clearNotify={clearNotify} {...notify} />
      <FormInput
        label={t('profile.firstName')}
        value={firstName}
        onSave={_saveFirstName}
        onChange={changeFirstName}
        isEdit={isEditFirstName}
        onEditMode={setIsEditFirstName}
        ref={firstNameRef}
        isSubmitting={firstNameSubmitting}
      />
      <FormInput
        label={t('profile.email')}
        value={email}
        onSave={_saveEmail}
        onChange={changeEmail}
        isEdit={isEditEmail}
        onEditMode={setIsEditEmail}
        ref={emailRef}
        isSubmitting={emailSubmitting}
      />
      <LanguageSection id='settings-language' />
    </>
  )
}

export default MyAccount
